import { CheckCircle2, AlertTriangle, Hourglass } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useReviewQueue } from '@/hooks/useReviewQueue';
import { useAuth } from '@/hooks/useAuth';
import { EmptyState } from '@/components/common/EmptyState';
import { ReviewQueueItem } from './ReviewQueueItem';
import type { ReviewQueueItem as ReviewQueueItemType } from '@/hooks/useReviewQueue';
import type { GateDecision } from '@/types';

// ─── Priority section config ─────────────────────────────────────────────────

type PrioritySection = {
  key: GateDecision;
  label: string;
  headerClass: string;
  badgeClass: string;
};

const PRIORITY_SECTIONS: PrioritySection[] = [
  {
    key: 'REJECTED',
    label: 'Rejected by AI',
    headerClass: 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  },
  {
    key: 'ESCALATED',
    label: 'Manual Review Required',
    headerClass: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-200',
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  },
  {
    key: 'MANUAL_REVIEW_REQUIRED',
    label: 'Manual Review Required (Circuit Breaker)',
    headerClass: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  },
  {
    key: 'APPROVED_WITH_ALERTS',
    label: 'Approved with Alerts',
    headerClass: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  },
  {
    key: 'APPROVED',
    label: 'Approved by AI — Human Sign-Off Required',
    headerClass: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  },
];

// ─── Priority section ─────────────────────────────────────────────────────────

interface PrioritySectionProps {
  section: PrioritySection;
  items: ReviewQueueItemType[];
  canClaim: boolean;
  currentUserId: string | undefined;
}

function PrioritySectionGroup({ section, items, canClaim, currentUserId }: PrioritySectionProps) {
  if (items.length === 0) return null;

  return (
    <section aria-label={section.label}>
      <div className={cn('mb-3 flex items-center justify-between rounded-lg border px-4 py-2', section.headerClass)}>
        <h3 className="text-sm font-semibold">{section.label}</h3>
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-bold', section.badgeClass)}>
          {items.length} contract{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <ReviewQueueItem key={item.id} item={item} canClaim={canClaim} currentUserId={currentUserId} />
        ))}
      </div>
    </section>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function QueueSkeleton() {
  return (
    <div role="status" aria-label="Loading review queue" className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-16 animate-pulse rounded-lg border border-border bg-muted" aria-hidden="true" />
      ))}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * Needs Review tab — FR-077 compliant: lists ALL gate decisions requiring
 * human sign-off, divided by priority (REJECTED first, then ESCALATED,
 * MANUAL_REVIEW_REQUIRED, APPROVED_WITH_ALERTS, APPROVED). No "auto-approved" path exists.
 *
 * Claim integration: "Start Review" claims then navigates; "Continue Review"
 * navigates directly for already-claimed items.
 * Aging indicator turns red when > 48h (FR-086 overdue threshold).
 */
export function NeedsReviewTab() {
  const { data: items, isLoading, error, refetch } = useReviewQueue();
  const { canReview, user } = useAuth();
  const canClaim = canReview;

  if (isLoading) return <QueueSkeleton />;

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
        <p className="font-medium flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          Failed to load review queue.
        </p>
        <button type="button" onClick={() => void refetch()} className="mt-2 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="All caught up!"
        description="No contracts need review right now."
      />
    );
  }

  // Items the AI gate hasn't decided on yet — they're paused mid-pipeline
  // (paused_at_document_assistant / document / parties / clause / etc.).
  // Without this section they'd never render because the priority sections
  // all key on a non-null final_decision.
  const inFlightItems = items.filter(
    (i) => i.final_decision == null && i.review_status.startsWith('paused_at_'),
  );

  return (
    <div aria-live="polite" className="space-y-6">
      {inFlightItems.length > 0 && (
        <section aria-label="Awaiting in pipeline">
          <div className="mb-3 flex items-center justify-between rounded-lg border px-4 py-2 border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <Hourglass className="h-4 w-4" aria-hidden="true" />
              Awaiting in pipeline — review at current stage
            </h3>
            <span className="rounded-full px-2 py-0.5 text-xs font-bold bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {inFlightItems.length} contract{inFlightItems.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-2">
            {inFlightItems.map((item) => (
              <ReviewQueueItem
                key={item.id}
                item={item}
                canClaim={canClaim}
                currentUserId={user?.id}
              />
            ))}
          </div>
        </section>
      )}
      {PRIORITY_SECTIONS.map((section) => {
        const sectionItems = items.filter((i) => i.final_decision === section.key);
        return (
          <PrioritySectionGroup
            key={section.key}
            section={section}
            items={sectionItems}
            canClaim={canClaim}
            currentUserId={user?.id}
          />
        );
      })}
    </div>
  );
}
